import { useFlags } from 'launchdarkly-react-client-sdk';
import * as React from 'react';
import * as _ from 'lodash';
import { useDispatch } from 'react-redux';
import { Redirect } from 'react-router-dom';
import {
  API,
  capitalizeFirstLetterEachWord,
  CustomerApproveConfirmation,
  CustomerStatus,
  CustomerSummary,
  getCreatePermission,
  getUuidV4,
  Logging,
  PermissionGroup,
  SelectorButton,
  SelectorButtonOption,
  UserContext,
  UserInfo,
  Notification,
  NotificationData,
  ResponseError,
  SettingsContextObject,
  getViewPermission,
  getEditPermission,
} from '@vcd-ecm-ui/Common';
import {
  GenericGrid,
  ActionMenu,
  ColumnLink,
  FilterModelApi,
  GridDataSummaryCollection,
  SortModelApi,
  Action,
  ActionDivider,
  mergeDefaultAndCustomColumns,
  getSelectFilterAsString,
  FilterTypeEnum,
  GridFilter,
  GridSort,
  SelectFilterType,
  getSelectFilterFromFilters,
} from '@vcd-ecm-ui/GenericGrid';
import { VtxButton, VtxColumnProps, VtxIcon } from '@vertexinc/vtx-ui-react-component-library';
import CustomerGridProps from './CustomerGridProps.interface';
import GridRowData, { GridDataNativeType } from './GridRowData';
import getDefaultColumns, { additionalTableSettings } from './defaultColumns';
import getDefaultColumnsCompact, { additionalTableSettingsCompact } from './defaultColumnsCompact';
import './common-reports.scss';
import './customer-grid.scss';

const CustomerGrid = ({
  gatewayEndPoint,
  displayGridOnly = false,
  parentCustomer = null,
}: CustomerGridProps): JSX.Element => {
  // Get the User Context
  const userContext: UserInfo = React.useContext(UserContext);
  const { ccPreventEcmEdits } = useFlags();

  const hasCustomerCreatePermission: boolean = getCreatePermission(userContext, PermissionGroup.Customers);
  const hasCustomerViewPermission: boolean = getViewPermission(userContext, PermissionGroup.Customers);
  const hasCustomerEditPermission: boolean = getEditPermission(userContext, PermissionGroup.Customers);

  const { settingsContext, setSettingsContext } = React.useContext(SettingsContextObject);
  const [redirect, setRedirect] = React.useState(null);
  const [notification, setNotification] = React.useState(null);
  const [isCustomerApproveConfirmationOpen, setIsCustomerApproveConfirmationOpen] = React.useState(false);
  const [selectedCustomer, setSelectedCustomer] = React.useState(null);
  const [manualRefreshKey, setManualRefreshKey] = React.useState(null);

  const dispatch = useDispatch();

  // Get the Status Filter
  const getStatusFilter = (status: string): GridFilter => {
    const statusFilter: GridFilter = {
      column: 'status',
      value: status === CustomerStatus.All
        ? null
        : getSelectFilterAsString(FilterTypeEnum.Text, null, false, [status], [capitalizeFirstLetterEachWord(status)]),
    };

    return statusFilter;
  };

  // Selected Status
  const [selectedStatus, setSelectedStatus] = React.useState(sessionStorage.customerStatus
    ? sessionStorage.customerStatus
    : CustomerStatus.Pending);

  // Create the External Status Filter
  const [externalFilter, setExternalFilter] = React.useState(getStatusFilter(selectedStatus));

  // Parse each row from the Native Data Type to the Actual Data Type
  const rowParser = (row: GridDataNativeType): GridRowData => GridRowData.fromData(row);

  // Customer Code Link Clicked
  const onCustomerCodeClick = (row: GridRowData): void => {
    const customerUuid: string = row.rowData.customerUuid; // eslint-disable-line prefer-destructuring

    Logging.Info('Customer Code Clicked:', customerUuid, row.rowData);

    setRedirect(`/customer/view/${customerUuid}/1`);
  };

  // Parent Customer Code Link Clicked
  const onParentCustomerCodeClick = (row: GridRowData): void => {
    const parentCustomerUuid: string = row.rowData.parentUuid;

    Logging.Info('Parent Customer Code Clicked:', parentCustomerUuid, row.rowData);

    setRedirect(`/customer/view/${parentCustomerUuid}/1`);
  };

  const onExemptionsClick = (row: GridRowData): void => {
    const customerUuid: string = row.rowData.customerUuid; // eslint-disable-line prefer-destructuring

    Logging.Info('Exemptions Clicked:', row.rowData);

    setRedirect(`/customer/view/${customerUuid}/3`);
  };

  // Make the API Call to Accept the Certificate
  const approveCustomer = (customerSummary: CustomerSummary) => async (): Promise<void> => {
    Logging.Info('Approve Customer', customerSummary);
    try {
      const api = new API(gatewayEndPoint);

      await api.customer.approveCustomerV4(customerSummary.customerUuid);

      const notificationData: NotificationData = {
        type: 'Success',
        title: 'Customer Approval Status',
        description: `Customer '${customerSummary.customerName}' was Approved.`,
        position: 'BottomRight',
      };

      setNotification(notificationData);

      // Refresh the Grid
      setManualRefreshKey(getUuidV4());
    } catch (error) {
      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Customer Approval Error',
        description: `Unable to approve Customer '${customerSummary.customerName}'.`,
        error: error as ResponseError,
      };

      setNotification(notificationData);
    }
  };

  // Approve Menu / Button clicked in Actions
  const onApprove = (customerSummary: CustomerSummary): void => {
    if (customerSummary) {
      // Save the Selected Customer
      setSelectedCustomer(customerSummary);

      // Check if the Customer has a Parent
      const hasParentCustomer = customerSummary.parentUuid !== null;

      if (hasParentCustomer) {
        // Show the Approve Confirmation Dialog
        setIsCustomerApproveConfirmationOpen(true);
      } else {
        // Dispatch the Asynchronous Event
        dispatch(approveCustomer(customerSummary));
      }
    }
  };

  const onApproveConfirmation = (): void => {
    Logging.Info('Approve Confirmation');

    // Hide the Approve Confirmation Dialog
    setIsCustomerApproveConfirmationOpen(false);

    // Dispatch the Asynchronous Event
    dispatch(approveCustomer(selectedCustomer));
  };

  const onCancelConfirmation = (): void => {
    Logging.Info('Cancel Confirmation');

    // Hide the Approve Confirmation Dialog
    setIsCustomerApproveConfirmationOpen(false);
  };

  // Generic Action Menu Handler
  const onActionMenuClick = (actionKey: string, row: GridRowData): void => {
    const customerUuid: string = row.rowData.customerUuid; // eslint-disable-line prefer-destructuring

    Logging.Info('Action Menu Clicked:', actionKey, customerUuid, row.rowData);

    switch (actionKey) {
      case 'view':
        Logging.Info('View Customer', customerUuid);
        setRedirect(`/customer/view/${customerUuid}/1`);
        break;
      case 'edit':
        Logging.Info('Edit Customer', customerUuid);
        setRedirect(`/customer/edit/${customerUuid}`);
        break;
      case 'approve':
        Logging.Info('Approve Customer', customerUuid);
        onApprove(row.rowData);
        break;
      default:
        break;
    }
  };

  // Get the Action Menu Actions for the Row
  const getActionMenuActions = (row: GridRowData): (Action | ActionDivider)[] => {
    const isPending = row.status === CustomerStatus.Pending;

    const actions: (Action | ActionDivider)[] = [];

    if (hasCustomerViewPermission) {
      actions.push(
        {
          key: 'view',
          name: 'View',
          onClick: onActionMenuClick,
          row,
        },
      );
    }

    if (hasCustomerEditPermission) {
      actions.push(new ActionDivider());
      actions.push(
        {
          key: 'edit',
          name: 'Edit',
          onClick: onActionMenuClick,
          row,
          disabled: (ccPreventEcmEdits === true),
        },
      );
      actions.push(new ActionDivider());
      actions.push(
        {
          key: 'approve',
          name: 'Approve',
          onClick: onActionMenuClick,
          row,
          disabled: !isPending || (ccPreventEcmEdits === true),
        },
      );
    }

    return actions;
  };

  // Get the Columns for the Grid
  const getColumns = (): VtxColumnProps<GridRowData>[] => {
    // Get the Default Columns
    const defaultColumns: VtxColumnProps<GridRowData>[] = displayGridOnly ? getDefaultColumnsCompact(settingsContext.customers) : getDefaultColumns(settingsContext.customers); // eslint-disable-line max-len

    // Column Columns with Dynamic Renderer
    const customColumns = [
      {
        dataIndex: 'action',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        render: (value: any, row: GridRowData, index: number) => <ActionMenu actions={getActionMenuActions(row)} />,
      },
      {
        dataIndex: 'customerCode',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        render: (value: any, row: GridRowData, index: number) => (
          <ColumnLink
            id="customerCodeLink"
            linkText={value}
            onClick={onCustomerCodeClick}
            row={row}
          />
        ),
      },
      {
        dataIndex: 'parentCustomerCode',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        render: (value: any, row: GridRowData, index: number) => (value
          ? <ColumnLink id="parentCustomerCodeLink" linkText={value} onClick={onParentCustomerCodeClick} row={row} />
          : <></>
        ),
      },
      {
        dataIndex: 'numCertificates',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        render: (value: any, row: GridRowData, index: number) => (row.numCertificates
          ? <ColumnLink id="exemptionsLink" icon="paper" iconPlacement="left" onClick={onExemptionsClick} row={row} />
          : <></>
        ),
      },
    ];

    // Column Columns with Dynamic Renderer
    const customColumnsCompact = [
      {
        dataIndex: 'customerCode',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        render: (value: any, row: GridRowData, index: number) => (
          <ColumnLink
            id="customerCodeLink"
            linkText={value}
            onClick={onCustomerCodeClick}
            row={row}
          />
        ),
      },
    ];

    // Merge the Custom Columns into the Default Columns
    return mergeDefaultAndCustomColumns(defaultColumns, displayGridOnly ? customColumnsCompact : customColumns);
  };

  // The API Call used to retrieve data for the Grid
  const apiGetRowsServerModel = (
    startRow:number,
    endRow:number,
    filterModel: FilterModelApi,
    sortModel: SortModelApi,
  ): Promise<GridDataSummaryCollection> => {
    const api = new API(gatewayEndPoint);

    let filterModelCopy: FilterModelApi = filterModel;

    if (displayGridOnly && parentCustomer) {
      // Create a Custom Filter using the Parent Customer uuid
      const additionalCustomerUuuidFilter = `(parentuuid eq '${parentCustomer}')`;

      if (filterModelCopy === null) {
        filterModelCopy = [];
      }

      filterModelCopy.push(additionalCustomerUuuidFilter);
    }

    return api.customer.getCustomersV41(startRow, endRow, filterModelCopy, sortModel);
  };

  // Status Selection Changed
  const onSelectStatus = (status: string): void => {
    setSelectedStatus(status);

    // Save the Status to the Session Storage
    sessionStorage.customerStatus = status;

    // Update the External Filter
    setExternalFilter(getStatusFilter(status));
  };

  // Filtering or Sorting in the Grid Changed
  const onFilterSortingChange = (filters: GridFilter[], sorters: GridSort[]): void => {
    Logging.Debug('onFilterSortingChange', filters, sorters);

    // Get the Status Filter
    const selectFilter: SelectFilterType = getSelectFilterFromFilters(filters, 'status');
    const status = selectFilter ? selectFilter.values[0] : CustomerStatus.All;

    // Update the Status Button
    setSelectedStatus(status);

    // Save the Status to the Session Storage
    sessionStorage.customerStatus = status;

    // Update the External Filter
    setExternalFilter(getStatusFilter(status));
  };

  // On Add Customer
  const onAddCustomer = (): void => {
    // Direct to Edit Customers
    setRedirect('/customer/add');
  };

  const onNotificationClose = (): void => {
    // Clear the Customers Notification
    setNotification(null);
  };

  React.useEffect(() => {
    // This Code will run once after the initial Render
    Logging.Debug('Customer Grid: Mount');

    return (): void => {
      // This Code will run once the component unmounts.
    };
  }, []);

  const customerStatusOptions: SelectorButtonOption[] = [
    {
      label: CustomerStatus.Pending,
      value: CustomerStatus.Pending,
      bulletClass: 'bullet-pending',
      statusClass: 'status-pending',
      userSettable: true,
    },
    {
      label: CustomerStatus.Approved,
      value: CustomerStatus.Approved,
      bulletClass: 'bullet-approved',
      statusClass: 'status-approved',
      userSettable: true,
    },
    {
      label: CustomerStatus.Draft,
      value: CustomerStatus.Draft,
      bulletClass: 'bullet-draft',
      statusClass: 'status-draft',
      userSettable: true,
    },
    {
      label: CustomerStatus.All,
      value: CustomerStatus.All,
      bulletClass: 'bullet-all',
      statusClass: 'status-all',
      userSettable: true,
    },
  ];

  const addCustomerEnabled = true;

  // Check if Helper Data is Loaded before Showing the Grid
  const dataLoaded = true;

  if (redirect) {
    return (
      <Redirect to={redirect} />
    );
  }

  return (
    <>
      <div className={displayGridOnly ? 'reportsContainerCompact' : 'reportsContainer'}>
        {
          !displayGridOnly && (
            <div className="reportsHeader">
              <div className="reportsHeaderLeft">
                Customers
                <SelectorButton
                  name="CustomerStatus"
                  onSelect={onSelectStatus}
                  options={customerStatusOptions}
                  selected={selectedStatus}
                />
              </div>
              <div className="reportsHeaderRight">
                {
                  hasCustomerCreatePermission && (
                    <VtxButton
                      id="addCustomerButton"
                      icon={<VtxIcon type="plus" />}
                      disabled={!addCustomerEnabled}
                      onClick={onAddCustomer}
                      type="primary"
                    >
                      Add Customer
                    </VtxButton>
                  )
                }
              </div>
            </div>
          )
        }
        {/* Change the Key in the Parent Div to Reload */}
        <div className={displayGridOnly ? 'reportsContentGridCompact' : 'reportsContentGrid'}>
          {
            dataLoaded && (
              <GenericGrid<GridRowData>
                id={displayGridOnly ? 'customerGridCompact' : 'customerGrid'}
                dataTypeDescription={displayGridOnly ? 'Child Customers' : 'Customers'}
                getDefaultColumns={getColumns}
                rowParser={rowParser}
                apiGetRowsServerModel={apiGetRowsServerModel}
                verticalHeightOffset={displayGridOnly ? 415 : 339} // This will handle double header line columns. Single: 30px, Double: 50px
                model="server"
                additionalTableSettings={displayGridOnly ? additionalTableSettingsCompact : additionalTableSettings}
                onFilterSortingChange={onFilterSortingChange}
                externalFilter={displayGridOnly ? null : externalFilter}
                alwaysHiddenFilterBreadcrumbs={displayGridOnly ? [] : ['status']}
                refreshKey={manualRefreshKey}
                showTableManagement={!displayGridOnly}
                showAllFavoriteColumns={!displayGridOnly}
                showClearFilters={!displayGridOnly}
                showClearSorters={!displayGridOnly}
                showLoadingIndicator
              />
            )
          }
        </div>
        <CustomerApproveConfirmation
          isOpen={isCustomerApproveConfirmationOpen}
          onCancel={onCancelConfirmation}
          onApprove={onApproveConfirmation}
        />
        <Notification
          data={notification}
          onNotificationClose={onNotificationClose}
          autoClose
        />
      </div>
    </>
  );
};

export default CustomerGrid;
